import i18next from "../i18next";
import {
  createDocumentErrorNotification,
  createSuccessNotification,
  createWarningNotification,
} from "../common/notifications/notification";

const DEFAULT_FILE_NAME = "download";

/**
 * RFC 6266 extended value: filename*=<charset>'<language>'<percent-encoded>.
 * The language part is optional but the quotes around it are not, and it is
 * legal for a server to send both filename and filename* in either order.
 */
const extendedFileName = (contentDisposition) => {
  // the (^|;) boundary keeps a parameter such as xfilename* from matching
  const match = contentDisposition.match(/(?:^|;)\s*filename\*\s*=\s*[^';]*'[^';]*'([^;]+)/i);
  if (!match) {
    return null;
  }

  try {
    return decodeURIComponent(match[1].trim());
  } catch (e) {
    // malformed percent-encoding - fall back to the plain filename
    return null;
  }
};

/**
 * Plain value, quoted or not. A quoted name may legally contain semicolons and
 * escaped quotes, so it has to be matched before the unquoted variant.
 */
const plainFileName = (contentDisposition) => {
  const quoted = contentDisposition.match(/(?:^|;)\s*filename\s*=\s*"((?:[^"\\]|\\.)*)"/i);
  if (quoted) {
    return quoted[1].replace(/\\(.)/g, "$1").trim();
  }

  const unquoted = contentDisposition.match(/(?:^|;)\s*filename\s*=\s*([^;]+)/i);
  return unquoted ? unquoted[1].trim() : null;
};

const fileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) {
    return null;
  }

  const fileName =
    extendedFileName(contentDisposition) ?? plainFileName(contentDisposition);

  return fileName || null;
};

export const resolveDownloadFileName = (
  response,
  fallbackFileName,
  { preferServerFileName = true } = {}
) => {
  if (!preferServerFileName && fallbackFileName) {
    return fallbackFileName;
  }

  return (
    fileNameFromContentDisposition(response?.headers?.["content-disposition"]) ??
    fallbackFileName ??
    DEFAULT_FILE_NAME
  );
};

/**
 * axios.defaults.validateStatus (src/axios.js) resolves everything below 500,
 * so a 4xx arrives here as a fulfilled response whose body is the JSON error
 * blob - without this guard it would be written to disk as if it were the
 * requested document. A 204 is a success status but carries no document.
 */
const noDocumentInResponse = async (response) => {
  if (response.status === 204) {
    createWarningNotification(
      i18next.t("notifications.noDocument"),
      i18next.t("notifications.warning")
    );
    return true;
  }

  if (response.status >= 200 && response.status < 300) {
    return false;
  }

  const error = new Error(`Request failed with status code ${response.status}`);
  error.response = response;
  await createDocumentErrorNotification(error);

  return true;
};

/**
 * Turns a blob response into an actual browser download and confirms it to the
 * user - Chrome/Edge keep the user inside the app, so the notification is the
 * only feedback that anything happened.
 *
 * The file name comes from the `content-disposition` header when the server
 * sends one; pass `preferServerFileName: false` to keep `fallbackFileName`
 * regardless, for exports whose name downstream consumers rely on.
 *
 * Returns false when no document was received; the user has already been
 * notified in that case.
 */
export const downloadFile = async (response, fallbackFileName, options) => {
  if (await noDocumentInResponse(response)) {
    return false;
  }

  const fileName = resolveDownloadFileName(response, fallbackFileName, options);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);

  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    // deferred so the browser has picked the blob up before it is released
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }

  createSuccessNotification(fileName, i18next.t("notifications.downloadStarted"));

  return true;
};

/** Exports whose file name downstream consumers may depend on. */
export const PINNED_FILE_NAME = { preferServerFileName: false };
